import React, { useEffect } from 'react';
import { FiX } from 'react-icons/fi';

interface OfferModalProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  maxWidthClass?: string;
}

export const OfferModal: React.FC<OfferModalProps> = ({
  title,
  subtitle,
  onClose,
  children,
  maxWidthClass = 'max-w-md',
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/80 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
        className={`w-full ${maxWidthClass} max-h-[90vh] overflow-y-auto my-auto rounded-3xl border border-[#2c2d33] bg-[#141518] p-4 sm:p-6 shadow-2xl space-y-5 text-white`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold">{title}</h3>
            {subtitle && <p className="text-xs text-zinc-400 mt-0.5">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-zinc-400 hover:text-white transition cursor-pointer flex-shrink-0"
          >
            <FiX size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export interface PaymentAmountOption {
  key: string;
  label: string;
  description?: string;
  amount: number;
  milestoneIds: number[];
}

export function PaymentOptionList({
  options,
  selectedKey,
  onSelect,
  currency,
  formatAmount,
}: {
  options: PaymentAmountOption[];
  selectedKey: string;
  onSelect: (key: string) => void;
  currency: string;
  formatAmount: (amount: number, currency: string) => string;
}) {
  return (
    <div className="space-y-1.5">
      {options.map((opt) => {
        const selected = opt.key === selectedKey;
        return (
          <label
            key={opt.key}
            className={`flex items-center gap-3 p-3 rounded-xl border transition cursor-pointer text-xs ${
              selected
                ? 'border-[#22c55e]/50 bg-[#22c55e]/10 text-white'
                : 'border-[#22242a] bg-[#0c0d0f] text-zinc-400 hover:text-zinc-200'
            }`}
          >
            <input
              type="radio"
              name="paymentAmountOption"
              checked={selected}
              onChange={() => onSelect(opt.key)}
              className="accent-[#22c55e]"
            />
            <span className="min-w-0 flex-1">
              <span className="block font-semibold">{opt.label}</span>
              {opt.description && (
                <span className="block text-[11px] text-zinc-500 mt-0.5 truncate">
                  {opt.description}
                </span>
              )}
            </span>
            <span className="font-bold tabular-nums text-white">
              {formatAmount(opt.amount, currency)}
            </span>
          </label>
        );
      })}
    </div>
  );
}
